import React, { useRef, useEffect, useState, useMemo } from 'react';
import SendIcon from '@mui/icons-material/Send';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import './ChatForm.css';
import { SemanticModel } from '../services/dto/semanticModel';

interface ChatFormProps {
  handleSubmitWrapper: (
    e: React.FormEvent<HTMLFormElement> | null,
    promptData?: string
  ) => void;
  handleStop?: () => void; // Prop for stop functionality
  prompt: string;
  fileName: string;
  setModelId: (modelId: string) => void;
  fileOptions: SemanticModel[] | null; // Updated to accept null
  isLoading: boolean;
  setHasStartedChat: (hasStarted: boolean) => void;
}

const ChatForm: React.FC<ChatFormProps> = ({
  handleSubmitWrapper,
  handleStop,
  prompt,
  fileName,
  setModelId,
  fileOptions,
  isLoading,
  setHasStartedChat,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [localPrompt, setLocalPrompt] = useState(prompt);


  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  //   useEffect(() => {
  //   adjustHeight();
  // }, [localPrompt]);

  // Automatically set the fileName if the corresponding file option exists
  useEffect(() => {
    const defaultFile =
      fileOptions &&
      fileOptions.find((file) => file.name === 'FULFILLMENT_ORDER_ITEMS.yaml');
    if (defaultFile && !fileName) {
      setModelId(defaultFile.id);
    }
  }, [fileOptions, fileName, setModelId]);

  // Effect to change hasStartedChat state when fileName changes
  useEffect(() => {
    if (fileName) {
      setHasStartedChat(true); // Set to true when a file is selected
    } else {
      setHasStartedChat(false); // Set to false if no file is selected
    }
  }, [fileName, setHasStartedChat]);

  useEffect(() => {
    adjustHeight();
  }, [localPrompt]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
      e.preventDefault();
      handleSubmitWrapper(null, localPrompt);
      setLocalPrompt('');
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission
    if (!isLoading) {
      handleSubmitWrapper(null, localPrompt);
      setLocalPrompt('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isLoading) {
      setLocalPrompt(e.target.value);
      setHasStartedChat(true);
      adjustHeight();
    }
  };

  // Sort options by file name for display and searching
  const sortedOptions = useMemo(() => {
    return (fileOptions ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [fileOptions]);

  // Helper function to get the appropriate placeholder text
  const getPlaceholderText = () => {
    // If fileOptions is null/undefined, still loading
    if (!fileOptions) {
      return 'Loading files...';
    }
    // If fileOptions is an empty array, no models available
    if (fileOptions.length === 0) {
      return "You don't have any semantic models available";
    }
    // If fileOptions has items, show select message
    return 'Select a Semantic Model';
  };

  return (
    <form className="chat-input-container" onSubmit={handleSubmit}>
      <div className="textarea-container">
        <textarea
          ref={textareaRef}
          placeholder="Type your message here..."
          value={localPrompt}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          className="chat-input"
          disabled={isLoading || !fileName}
        />
        <IconButton
          type="submit"
          className="submit-button"
          disabled={isLoading}
          aria-label="send"
        >
          {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
        </IconButton>
      </div>
      <Autocomplete
        options={sortedOptions}
        getOptionLabel={(option) => option.name}
        value={sortedOptions.find((o) => o.id === fileName) ?? null}
        disabled={sortedOptions.length === 0 || isLoading}
        onChange={(_e, value) => {
          if (!isLoading) {
            setModelId(value?.id ?? '');
            setHasStartedChat(!!value?.id);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={getPlaceholderText()}
            size="small"
            variant="outlined"
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <div className="file-option">
              <div className="file-name">{option.name}</div>
              {option.description && (
                <div className="file-description">{option.description}</div>
              )}
            </div>
          </li>
        )}
        className={`file-select ${isLoading ? 'loading' : ''}`}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
      />
    </form>
  );
};

export default ChatForm;
